import { Component } from "../component"
import { iterable } from "../helpers/iterable"
import { gsap, ScrollToPlugin, ScrollTrigger } from "gsap/all"
import { deviceWidth } from "../helpers/device-size"

gsap.registerPlugin(ScrollToPlugin, ScrollTrigger)

export class BlogHero extends Component {
  constructor(props) {
    super(props)
  }

  ui() {
    return {
      hero: ".blog-hero",
      scrollButton: ".blog-hero-scroll-btn",
      quote: ".blog-quote",
      logo: ".blog-hero-logo",
      featuredArticle: ".blog-hero-article-1",
      article: ".blog-hero-article",
      image: ".blog-hero-image",
      footer: ".blog-hero-footer",
      footerLink: ".blog-hero-footer-link",
    }
  }

  calculateMovementFromHeroCenter(
    element,
    moveTowardsCenter,
    movementX = 100,
    movementY = 100
  ) {
    const { hero } = this.ui()
    const heroRect = document.querySelector(hero).getBoundingClientRect()

    const rect = element.getBoundingClientRect()

    const centerX = heroRect.left + heroRect.width / 2
    const centerY = heroRect.top + heroRect.height / 2

    const xDirection =
      rect.left < centerX === moveTowardsCenter
        ? `+=${movementX}`
        : `-=${movementX}`
    const yDirection =
      rect.top < centerY === moveTowardsCenter
        ? `+=${movementY}`
        : `-=${movementY}`

    return { xDirection, yDirection }
  }

  calculateMovementFromTarget(element, targetElement) {
    const rect = element.getBoundingClientRect()
    const targetRect = targetElement.getBoundingClientRect()

    const directionX = rect.x > targetRect.x ? 1 : -1
    const directionY = rect.y > targetRect.y ? 1 : -1

    const distanceX = Math.abs(rect.x - targetRect.x)
    const distanceY = Math.abs(rect.y - targetRect.y)
    const viewportWidth = window.innerWidth * 0.2

    let shiftX, shiftY

    if (distanceX < viewportWidth && distanceY < viewportWidth) {
      shiftX = Math.floor(Math.random() * (150 - 70)) + 70
      shiftY = Math.floor(Math.random() * (150 - 70)) + 70
    } else {
      shiftX = Math.floor(Math.random() * (80 - 30)) + 30
      shiftY = Math.floor(Math.random() * (80 - 30)) + 30
    }

    return {
      xDirection: directionX * shiftX,
      yDirection: directionY * shiftY,
    }
  }

  initialAnimation() {
    const { logo, featuredArticle, article, hero } = this.ui()
    const deviceType = deviceWidth()
    const articlesEl = document.querySelectorAll(article)
    const featuredArticleScale = deviceType === "DESKTOP_XXL" ? 0.2 : 0.25

    if (deviceType === "PHONE" || deviceType === "TABLET") {
      return
    }

    gsap.to(logo, {
      width: "100%",
      duration: 1.5,
      delay: 1,
      ease: "power1.inOut",
    })

    gsap.to(featuredArticle, {
      scale: featuredArticleScale,
      duration: 1.5,
      delay: 1,
      ease: "power1.inOut",
      onComplete: () =>
        (document.querySelector(hero).style.overflow = "visible"),
    })

    Array.from(articlesEl)
      .slice(1)
      .forEach((article, index) => {
        const movement =
          index === 0 || index === 2 || index === 3 ? "20vw" : "10vw"
        const { xDirection, yDirection } = this.calculateMovementFromHeroCenter(
          article,
          false,
          movement,
          movement
        )

        gsap.from(article, {
          x: xDirection,
          y: yDirection,
          duration: 1.2,
          delay: 1.3,
          ease: "power1.inOut",
        })
      })
  }

  hoverAnimation() {
    const { article, logo, footer, footerLink, image, scrollButton } = this.ui()
    const articlesEl = document.querySelectorAll(article)

    const deviceType = deviceWidth()

    if (deviceType === "PHONE" || deviceType === "TABLET") {
      return
    }

    const featuredArticleScaleTo = deviceType === "DESKTOP_XXL" ? 0.25 : 0.3

    Array.from(articlesEl).forEach((articleEl, index) => {
      articleEl.isAnimating = false

      const footerEl = articleEl.querySelector(footer)
      const footerLinkEl = articleEl.querySelector(footerLink)
      const imageEl = articleEl.querySelector(image)

      const isFeaturedImg = index === 0
      const originalScale = gsap.getProperty(articleEl, "scale")

      const { xDirection, yDirection } = this.calculateMovementFromHeroCenter(
        articleEl,
        true,
        150,
        150
      )

      imageEl.addEventListener("mouseenter", () => {
        if (articleEl.isAnimating) {
          return
        }
        articleEl.isAnimating = true

        gsap.to(articleEl, {
          x: !isFeaturedImg && xDirection,
          y: !isFeaturedImg && yDirection,
          scale: !isFeaturedImg ? 1 : featuredArticleScaleTo,
          duration: 1,
          zIndex: 1,
          ease: "power1.inOut",
          overwrite: "auto",
        })

        gsap.to(footerEl, {
          opacity: 1,
          duration: 1,
          ease: "power1.inOut",
          onStart: () => (footerLinkEl.style.pointerEvents = "auto"),
        })

        Array.from(articlesEl).forEach((currentArticleEl) => {
          if (currentArticleEl !== articleEl) {
            const { xDirection, yDirection } = this.calculateMovementFromTarget(
              currentArticleEl,
              articleEl
            )

            gsap.to(currentArticleEl, {
              x: xDirection,
              y: yDirection,
              opacity: 0.3,
              duration: 1,
              ease: "power1.inOut",
            })
          }
        })
        gsap.to(logo, {
          opacity: 0.2,
          duration: 1,
          zIndex: 2,
          ease: "power1.inOut",
        })

        if (isFeaturedImg) {
          gsap.to(scrollButton, {
            opacity: 0,
            duration: 1,
            zIndex: 2,
            ease: "power1.inOut",
          })
        }
      })

      articleEl.addEventListener("mouseleave", () => {
        const isFeaturedImg = index === 0

        gsap.to(articleEl, {
          scale: originalScale,
          y: 0,
          x: 0,
          duration: 1,
          zIndex: 0,
          ease: "power1.inOut",
          overwrite: "auto",
        })

        gsap.to(footerEl, {
          opacity: 0,
          duration: 1,
          ease: "power1.inOut",
          onStart: () => (footerLinkEl.style.pointerEvents = "none"),
        })

        Array.from(articlesEl).forEach((currentArticleEl) => {
          if (currentArticleEl !== articleEl) {
            gsap.to(currentArticleEl, {
              y: 0,
              x: 0,
              opacity: 1,
              duration: 1,
              ease: "power1.inOut",
            })
          }
        })

        gsap.to(logo, {
          opacity: 1,
          duration: 1,
          ease: "power1.inOut",
          onComplete: () => (articleEl.isAnimating = false),
        })

        if (isFeaturedImg) {
          gsap.to(scrollButton, {
            opacity: 1,
            duration: 1,
            ease: "power1.inOut",
          })
        }
      })
    })
  }

  scrollToQuote() {
    const { quote, article } = this.ui()

    const articlesEl = document.querySelectorAll(article)

    gsap.to(window, {
      duration: 1.9,
      scrollTo: quote,
      ease: "power2.inOut",
    })

    const moveValues = [60, 200, 70, 300, 80, 160, 40]

    Array.from(articlesEl).forEach((articleEl, index) => {
      const yStart = gsap.getProperty(articleEl, "y")

      gsap.to(articleEl, {
        y: `-=${moveValues[index]}px`,
        duration: 1.5,
        ease: "power1.inOut",
        onComplete: () => {
          gsap.set(articleEl, { y: yStart })
        },
      })
    })
  }

  createParallaxEffect() {
    const deviceType = deviceWidth()
    if (deviceType === "PHONE" || deviceType === "TABLET") {
      return
    }

    const { article, hero, scrollButton } = this.ui()

    const elements = document.querySelectorAll(`${article}, ${scrollButton}`)

    const moveValues = [300, 350, 400, 200, 150, 250, 400, 250]

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: hero,
        start: "top top",
        end: "bottom-=10% top",
        scrub: 1.5,
      },
    })

    Array.from(elements).forEach((element, index) => {
      const movement = -moveValues[index]
      tl.fromTo(
        element,
        {
          y: 0,
        },
        {
          ease: "none",
          y: movement,
        },
        0
      )
    })
  }

  attachEvents() {
    const { scrollButton } = this.ui()
    const buttons = document.querySelectorAll(scrollButton)

    iterable(buttons).forEach((button) => {
      button.addEventListener("click", (e) => {
        this.scrollToQuote()
        e.preventDefault()
      })
    })
  }

  created() {
    this.initialAnimation()
    setTimeout(() => {
      this.attachEvents()
      this.hoverAnimation()
      this.createParallaxEffect()
    }, 2600)
  }
}
